import {
  Box,
  Button,
  Card,
  Container,
  Group,
  HoverCard,
  Select,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuCalendar, LuChevronLeft, LuChevronRight, LuMap } from 'react-icons/lu'
import { bookingStatusColor } from '@/frontend/components/marina/colors'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { usePontoons } from '@/frontend/hooks/useBerths'
import { useOccupancyCalendar } from '@/frontend/hooks/useOccupancy'

export const Route = createFileRoute('/dashboard/occupancy/calendar')({
  component: CalendarPage,
})

const CELL_WIDTH = 68
const ROW_HEIGHT = 38
const LABEL_WIDTH = 88

// Solid bar fill colors (tone 4) for each booking status
const BAR_FILL: Record<string, string> = {
  PENDING: 'var(--mantine-color-yellow-3)',
  WAITLISTED: 'var(--mantine-color-gray-3)',
  CONFIRMED: 'var(--mantine-color-blue-3)',
  DECLINED: 'var(--mantine-color-red-3)',
  RESERVED: 'var(--mantine-color-teal-3)',
  CHECKED_IN: 'var(--mantine-color-green-3)',
  CHECKED_OUT: 'var(--mantine-color-gray-2)',
  CANCELLED: 'var(--mantine-color-red-2)',
  NO_SHOW: 'var(--mantine-color-red-2)',
}

const QUICK_RANGES = [
  { label: '7h', days: 7 },
  { label: '14h', days: 14 },
  { label: '30h', days: 30 },
]

function CalendarPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canWrite = useCanWrite()

  const [from, setFrom] = useState(dayjs().format('YYYY-MM-DD'))
  const [to, setTo] = useState(dayjs().add(13, 'day').format('YYYY-MM-DD'))
  const [pontoonId, setPontoonId] = useState<string | null>(null)

  const { data: pontoonsData } = usePontoons()
  const pontoons = pontoonsData?.pontoons ?? []
  const { data, isLoading } = useOccupancyCalendar({ from, to, pontoonId: pontoonId ?? undefined })

  const days = useMemo(() => {
    const result: string[] = []
    let cur = dayjs(from)
    const end = dayjs(to)
    while (cur.isBefore(end) || cur.isSame(end, 'day')) {
      result.push(cur.format('YYYY-MM-DD'))
      cur = cur.add(1, 'day')
    }
    return result
  }, [from, to])

  const today = dayjs().format('YYYY-MM-DD')
  const todayIdx = days.indexOf(today)
  const rangeSpan = days.length

  const berths = useMemo(
    () =>
      [...(data?.berths ?? [])].sort((a, b) =>
        (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }),
      ),
    [data?.berths],
  )
  const bookings = data?.bookings ?? []

  const perBerth = useMemo(() => {
    const map = new Map<string, typeof bookings>()
    for (const b of bookings) {
      const arr = map.get(b.berthId) ?? []
      arr.push(b)
      map.set(b.berthId, arr)
    }
    return map
  }, [bookings])

  // Stats
  const visibleBookings = bookings.filter((bk) => {
    const ci = dayjs(bk.checkInAt)
    const co = dayjs(bk.checkOutAt)
    return ci.isBefore(dayjs(to).endOf('day')) && co.isAfter(dayjs(from).startOf('day'))
  })
  const uniqueBoats = new Set(visibleBookings.map((b) => b.boat.name)).size

  function shiftRange(delta: number) {
    const newFrom = dayjs(from).add(delta, 'day').format('YYYY-MM-DD')
    const newTo = dayjs(to).add(delta, 'day').format('YYYY-MM-DD')
    setFrom(newFrom)
    setTo(newTo)
  }

  function setQuickRange(n: number) {
    setFrom(dayjs().format('YYYY-MM-DD'))
    setTo(
      dayjs()
        .add(n - 1, 'day')
        .format('YYYY-MM-DD'),
    )
  }

  const rangeLabel = (() => {
    const f = dayjs(from)
    const t = dayjs(to)
    if (f.month() === t.month() && f.year() === t.year()) return f.format('MMMM YYYY')
    if (f.year() === t.year()) return `${f.format('MMM')} – ${t.format('MMM YYYY')}`
    return `${f.format('MMM YYYY')} – ${t.format('MMM YYYY')}`
  })()

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuCalendar size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.occupancy.calendar.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {rangeLabel} · {rangeSpan} hari
              </Text>
            </div>
          </Group>

          {canWrite && (
            <Button color="karang" size="sm" onClick={() => navigate({ to: '/dashboard/bookings/new' as any })}>
              {t('page.occupancy.calendar.btn_new')}
            </Button>
          )}
        </Group>

        {/* Stats strip */}
        {!isLoading && berths.length > 0 && (
          <Group gap="sm">
            <Card radius="md" padding="xs" px="md">
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.calendar.count_berths')}
                </Text>
                <Text size="xs" fw={700}>
                  {berths.length}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="xs" px="md">
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.calendar.count_bookings')}
                </Text>
                <Text size="xs" fw={700}>
                  {visibleBookings.length}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="xs" px="md">
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.calendar.count_boats')}
                </Text>
                <Text size="xs" fw={700}>
                  {uniqueBoats}
                </Text>
              </Group>
            </Card>
          </Group>
        )}

        {/* Controls */}
        <Group gap="sm" justify="space-between" wrap="wrap">
          <Group gap="sm" wrap="wrap">
            {/* Map / Timeline toggle */}
            <Group gap={4}>
              <Button
                size="xs"
                variant="default"
                leftSection={<LuMap size={13} />}
                onClick={() => navigate({ to: '/dashboard/occupancy/map' as any })}
              >
                Map
              </Button>
              <Button size="xs" variant="filled" color="laut" leftSection={<LuCalendar size={13} />}>
                Timeline
              </Button>
            </Group>

            {/* Quick range shortcuts */}
            <Group gap={4}>
              {QUICK_RANGES.map((r) => {
                const active = rangeSpan === r.days && from === dayjs().format('YYYY-MM-DD')
                return (
                  <UnstyledButton
                    key={r.label}
                    onClick={() => setQuickRange(r.days)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: '0.78rem',
                      fontWeight: active ? 600 : 500,
                      background: active ? 'var(--mantine-color-laut-6)' : 'transparent',
                      color: active ? 'white' : 'var(--mantine-color-dimmed)',
                      transition: 'background 120ms ease',
                    }}
                  >
                    {r.label}
                  </UnstyledButton>
                )
              })}
            </Group>

            {/* Prev / Next navigation */}
            <Group gap={2}>
              <Tooltip label={`← ${rangeSpan} hari sebelumnya`} withArrow>
                <Button size="xs" variant="default" px="xs" onClick={() => shiftRange(-rangeSpan)}>
                  <LuChevronLeft size={14} />
                </Button>
              </Tooltip>
              <Tooltip label={t('page.occupancy.calendar.btn_today')} withArrow>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => setFrom(dayjs().format('YYYY-MM-DD'))}
                  disabled={from === dayjs().format('YYYY-MM-DD')}
                >
                  {t('page.occupancy.calendar.btn_today')}
                </Button>
              </Tooltip>
              <Tooltip label={`${rangeSpan} hari berikutnya →`} withArrow>
                <Button size="xs" variant="default" px="xs" onClick={() => shiftRange(rangeSpan)}>
                  <LuChevronRight size={14} />
                </Button>
              </Tooltip>
            </Group>
          </Group>

          <Group gap="sm" wrap="wrap">
            <Group gap={4} align="center">
              <Text size="xs" c="dimmed">
                Dari
              </Text>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                style={{
                  fontSize: '0.8rem',
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--mantine-color-default-border)',
                  background: 'var(--app-card-bg)',
                  color: 'inherit',
                }}
              />
            </Group>
            <Group gap={4} align="center">
              <Text size="xs" c="dimmed">
                s/d
              </Text>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={{
                  fontSize: '0.8rem',
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--mantine-color-default-border)',
                  background: 'var(--app-card-bg)',
                  color: 'inherit',
                }}
              />
            </Group>
            <Select
              placeholder={t('page.occupancy.calendar.placeholder_pontoon')}
              clearable
              data={pontoons.map((p) => ({ value: p.id, label: `${p.code}${p.name ? ` – ${p.name}` : ''}` }))}
              value={pontoonId}
              onChange={setPontoonId}
              size="xs"
              w={200}
            />
          </Group>
        </Group>

        {/* Calendar grid */}
        <Card radius="md" padding={0} style={{ overflow: 'hidden' }}>
          <Box style={{ overflow: 'auto', position: 'relative' }}>
            <Box style={{ display: 'inline-block', minWidth: '100%', position: 'relative' }}>
              {/* Header row */}
              <Group
                gap={0}
                wrap="nowrap"
                style={{
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                  position: 'sticky',
                  top: 0,
                  background: 'var(--app-card-bg)',
                  zIndex: 2,
                }}
              >
                <Box
                  style={{
                    width: LABEL_WIDTH,
                    minWidth: LABEL_WIDTH,
                    padding: '8px 12px',
                    borderRight: '1px solid var(--mantine-color-default-border)',
                    position: 'sticky',
                    left: 0,
                    background: 'var(--app-card-bg)',
                    zIndex: 3,
                  }}
                >
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                    Berth
                  </Text>
                </Box>
                {days.map((d) => {
                  const isToday = d === today
                  const isWeekend = dayjs(d).day() === 0 || dayjs(d).day() === 6
                  return (
                    <Box
                      key={d}
                      style={{
                        width: CELL_WIDTH,
                        minWidth: CELL_WIDTH,
                        padding: '6px 4px',
                        borderRight: '1px solid var(--mantine-color-default-border)',
                        textAlign: 'center',
                        background: isToday
                          ? 'var(--mantine-color-laut-0)'
                          : isWeekend
                            ? 'light-dark(rgba(0,0,0,0.02), rgba(255,255,255,0.02))'
                            : undefined,
                      }}
                    >
                      <Text size="xs" c={isToday ? 'laut.7' : isWeekend ? 'dimmed' : 'dimmed'}>
                        {dayjs(d).format('ddd')}
                      </Text>
                      <Text
                        size="sm"
                        fw={isToday ? 800 : 500}
                        c={isToday ? 'laut.7' : isWeekend ? 'dimmed' : undefined}
                      >
                        {dayjs(d).format('D')}
                      </Text>
                    </Box>
                  )
                })}
              </Group>

              {/* Today marker line */}
              {todayIdx >= 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 56,
                    bottom: 0,
                    left: LABEL_WIDTH + todayIdx * CELL_WIDTH + CELL_WIDTH / 2,
                    width: 2,
                    background: 'var(--mantine-color-laut-5)',
                    opacity: 0.5,
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />
              )}

              {/* Loading skeletons */}
              {isLoading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <Group
                    // biome-ignore lint/suspicious/noArrayIndexKey: fixed skeleton count, index is stable identity
                    key={i}
                    gap={0}
                    wrap="nowrap"
                    style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
                  >
                    <Box
                      style={{
                        width: LABEL_WIDTH,
                        minWidth: LABEL_WIDTH,
                        height: ROW_HEIGHT,
                        padding: '8px 12px',
                        borderRight: '1px solid var(--mantine-color-default-border)',
                      }}
                    >
                      <Skeleton height={14} width={48} radius="sm" />
                    </Box>
                    <Box style={{ flex: 1, height: ROW_HEIGHT, padding: '8px 16px' }}>
                      <Skeleton height={18} width={`${30 + (i % 4) * 15}%`} radius="sm" />
                    </Box>
                  </Group>
                ))}

              {/* Berth rows */}
              {!isLoading &&
                berths.map((b, rowIdx) => {
                  const bks = perBerth.get(b.id) ?? []
                  const isAlt = rowIdx % 2 === 1
                  return (
                    <Group
                      key={b.id}
                      gap={0}
                      wrap="nowrap"
                      style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
                    >
                      {/* Label */}
                      <Box
                        style={{
                          width: LABEL_WIDTH,
                          minWidth: LABEL_WIDTH,
                          padding: '0 12px',
                          borderRight: '1px solid var(--mantine-color-default-border)',
                          position: 'sticky',
                          left: 0,
                          background: isAlt
                            ? 'light-dark(rgba(0,0,0,0.015), rgba(255,255,255,0.015))'
                            : 'var(--app-card-bg)',
                          zIndex: 1,
                          height: ROW_HEIGHT,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Text size="sm" fw={600}>
                          {b.code}
                        </Text>
                      </Box>

                      {/* Timeline area */}
                      <Box
                        style={{
                          position: 'relative',
                          width: days.length * CELL_WIDTH,
                          minWidth: days.length * CELL_WIDTH,
                          height: ROW_HEIGHT,
                          background: isAlt ? 'light-dark(rgba(0,0,0,0.008), rgba(255,255,255,0.008))' : undefined,
                        }}
                      >
                        {/* Grid columns */}
                        {days.map((d, idx) => {
                          const isToday = d === today
                          const isWeekend = dayjs(d).day() === 0 || dayjs(d).day() === 6
                          return (
                            <div
                              // biome-ignore lint/suspicious/noArrayIndexKey: visual grid columns
                              key={idx}
                              style={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: idx * CELL_WIDTH,
                                width: CELL_WIDTH,
                                borderRight: '1px solid var(--mantine-color-default-border)',
                                background: isToday
                                  ? 'var(--mantine-color-laut-0)'
                                  : isWeekend
                                    ? 'light-dark(rgba(0,0,0,0.015), rgba(255,255,255,0.01))'
                                    : undefined,
                                opacity: isToday ? 0.5 : 1,
                              }}
                            />
                          )
                        })}

                        {/* Booking bars */}
                        {bks.map((bk) => {
                          const rangeStart = dayjs(from).startOf('day')
                          const rangeEnd = dayjs(to).endOf('day')
                          const ci = dayjs(bk.checkInAt)
                          const co = dayjs(bk.checkOutAt)
                          const bkFrom = ci.isBefore(rangeStart) ? rangeStart : ci
                          const bkTo = co.isAfter(rangeEnd) ? rangeEnd : co
                          const startIdx = bkFrom.diff(rangeStart, 'day', true)
                          const widthDays = bkTo.diff(bkFrom, 'day', true)
                          if (widthDays <= 0) return null
                          const color = bookingStatusColor[bk.status] ?? 'gray'
                          const fillBg = BAR_FILL[bk.status] ?? 'var(--mantine-color-gray-3)'
                          return (
                            <HoverCard key={bk.id} width={300} withArrow openDelay={100}>
                              <HoverCard.Target>
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 5,
                                    bottom: 5,
                                    left: startIdx * CELL_WIDTH + 2,
                                    width: widthDays * CELL_WIDTH - 4,
                                    background: fillBg,
                                    borderRadius: 5,
                                    padding: '2px 7px',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    zIndex: 2,
                                  }}
                                >
                                  <Box
                                    w={6}
                                    h={6}
                                    style={{
                                      borderRadius: '50%',
                                      background: `var(--mantine-color-${color}-6)`,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Text size="xs" fw={600} truncate style={{ lineHeight: 1.2 }}>
                                    {bk.boat.name}
                                  </Text>
                                </div>
                              </HoverCard.Target>
                              <HoverCard.Dropdown>
                                <Stack gap="xs">
                                  <Group justify="space-between" align="flex-start">
                                    <div>
                                      <Text fw={700} size="sm">
                                        {bk.boat.name}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        {bk.code}
                                      </Text>
                                    </div>
                                    <StatusBadge status={bk.status} kind="booking" upper />
                                  </Group>
                                  <Text size="sm">
                                    {bk.customer.firstName} {bk.customer.lastName}
                                  </Text>
                                  <Group gap={4}>
                                    <Text size="xs" c="dimmed">
                                      {dayjs(bk.checkInAt).format('DD MMM HH:mm')}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      →
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {dayjs(bk.checkOutAt).format('DD MMM HH:mm')}
                                    </Text>
                                  </Group>
                                  <Text
                                    size="xs"
                                    c="laut"
                                    fw={500}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate({ to: `/dashboard/reservations/${bk.id}` as any })}
                                  >
                                    Lihat reservasi →
                                  </Text>
                                </Stack>
                              </HoverCard.Dropdown>
                            </HoverCard>
                          )
                        })}
                      </Box>
                    </Group>
                  )
                })}

              {!isLoading && berths.length === 0 && (
                <Box p="xl" ta="center">
                  <Text c="dimmed" size="sm">
                    Tidak ada berth untuk filter ini
                  </Text>
                </Box>
              )}
            </Box>
          </Box>

          {/* Legend */}
          <Group
            gap="lg"
            px="md"
            py="xs"
            wrap="wrap"
            style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
          >
            {[
              { status: 'CONFIRMED', label: 'Dikonfirmasi' },
              { status: 'RESERVED', label: 'Dipesan' },
              { status: 'CHECKED_IN', label: 'Check-in' },
              { status: 'PENDING', label: 'Pending' },
            ].map((s) => {
              const color = bookingStatusColor[s.status] ?? 'gray'
              const fill = BAR_FILL[s.status] ?? 'var(--mantine-color-gray-3)'
              return (
                <Group key={s.status} gap={6} wrap="nowrap">
                  <Box
                    style={{
                      width: 20,
                      height: 10,
                      borderRadius: 3,
                      background: fill,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 4,
                      flexShrink: 0,
                    }}
                  >
                    <Box w={5} h={5} style={{ borderRadius: '50%', background: `var(--mantine-color-${color}-6)` }} />
                  </Box>
                  <Text size="xs" c="dimmed">
                    {s.label}
                  </Text>
                </Group>
              )
            })}
            {todayIdx >= 0 && (
              <Group gap={6} wrap="nowrap">
                <Box w={2} h={10} style={{ background: 'var(--mantine-color-laut-5)', borderRadius: 2 }} />
                <Text size="xs" c="dimmed">
                  Hari ini
                </Text>
              </Group>
            )}
            {berths.length > 0 && (
              <Text size="xs" c="dimmed" style={{ marginLeft: 'auto' }}>
                {days.length} hari · {berths.length} berth
              </Text>
            )}
          </Group>
        </Card>
      </Stack>
    </Container>
  )
}
